import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import PageHeader from "@/components/PageHeader";
import { Plus, PencilSimple, Trash } from "@phosphor-icons/react";

const EMPTY = { name: "", contact: "", email: "", phone: "", address: "", notes: "", ddt_email_template_id: "" };

export default function Customers() {
  const [rows, setRows] = useState([]);
  const [editing, setEditing] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [templates, setTemplates] = useState([]);

  const load = async () => {
    try {
      const [cu, tp] = await Promise.all([
        api.get("/customers"),
        api.get("/ddt-templates"),
      ]);
      setRows(cu.data);
      setTemplates(tp.data || []);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, []);

  const defaultTpl = templates.find((t) => t.is_default);

  const openNew = () => {
    setEditing(null);
    setForm({ ...EMPTY, ddt_email_template_id: defaultTpl?.id || "" });
    setShowForm(true);
  };
  const openEdit = (r) => {
    setEditing(r);
    setForm({
      name: r.name || "", contact: r.contact || "", email: r.email || "",
      phone: r.phone || "", address: r.address || "", notes: r.notes || "",
      ddt_email_template_id: r.ddt_email_template_id || defaultTpl?.id || "",
    });
    setShowForm(true);
  };

  const submit = async (e) => {
    e.preventDefault();
    try {
      if (editing) {
        await api.put(`/customers/${editing.id}`, form);
        toast.success("Cliente aggiornato");
      } else {
        await api.post("/customers", form);
        toast.success("Cliente creato");
      }
      setShowForm(false);
      load();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const remove = async (r) => {
    if (!window.confirm(`Eliminare il cliente "${r.name}"?`)) return;
    try {
      await api.delete(`/customers/${r.id}`);
      toast.success("Cliente eliminato");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div>
      <PageHeader
        title="Clienti"
        subtitle="Anagrafica dei clienti destinatari di uscite da magazzino fisico"
        action={
          <button
            data-testid="new-customer-btn"
            onClick={openNew}
            className="text-sm px-4 py-2.5 bg-[#002FA7] hover:bg-[#002480] text-white transition-colors duration-150 flex items-center gap-2"
          >
            <Plus size={16} weight="bold" /> Nuovo Cliente
          </button>
        }
      />
      <div className="p-8">
        <div className="bg-white border border-neutral-200 overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-neutral-50 border-b border-neutral-200">
              <tr className="text-left">
                <th className="px-4 py-3 label-eyebrow">Nome</th>
                <th className="px-4 py-3 label-eyebrow">Referente</th>
                <th className="px-4 py-3 label-eyebrow">Email</th>
                <th className="px-4 py-3 label-eyebrow">Telefono</th>
                <th className="px-4 py-3 label-eyebrow">Indirizzo</th>
                <th className="px-4 py-3 label-eyebrow text-right">Azioni</th>
              </tr>
            </thead>
            <tbody>
              {rows.length === 0 ? (
                <tr><td colSpan="6" className="px-4 py-12 text-center text-neutral-500">Nessun cliente. Aggiungi il primo.</td></tr>
              ) : rows.map((r) => (
                <tr key={r.id} className="border-b border-neutral-100 hover:bg-neutral-50 transition-colors">
                  <td className="px-4 py-3 font-medium">{r.name}</td>
                  <td className="px-4 py-3 text-neutral-600">{r.contact || "—"}</td>
                  <td className="px-4 py-3 text-neutral-600 font-mono text-xs">{r.email || "—"}</td>
                  <td className="px-4 py-3 text-neutral-600 font-mono text-xs">{r.phone || "—"}</td>
                  <td className="px-4 py-3 text-neutral-600 truncate max-w-xs">{r.address || "—"}</td>
                  <td className="px-4 py-3 text-right whitespace-nowrap">
                    <button data-testid={`edit-customer-${r.id}`} onClick={() => openEdit(r)} className="p-1.5 hover:bg-neutral-200 transition-colors mr-1"><PencilSimple size={16} /></button>
                    <button data-testid={`delete-customer-${r.id}`} onClick={() => remove(r)} className="p-1.5 hover:bg-red-100 text-red-600 transition-colors"><Trash size={16} /></button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {showForm && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-2 md:p-4">
          <form onSubmit={submit} className="bg-white border border-neutral-200 shadow-xl w-full max-w-lg max-h-[95vh] flex flex-col overflow-y-auto" data-testid="customer-form">
            <div className="p-6 border-b border-neutral-200">
              <div className="label-eyebrow mb-1">{editing ? "Modifica" : "Nuovo"}</div>
              <h2 className="font-display font-bold text-xl tracking-tight">Cliente</h2>
            </div>
            <div className="p-6 space-y-4 grid grid-cols-1 md:grid-cols-2 gap-4">
              <label className="block md:col-span-2">
                <span className="label-eyebrow block mb-2">Nome / Ragione sociale *</span>
                <input required data-testid="customer-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="w-full px-3 py-2 border border-neutral-300 text-sm focus:outline-none focus:ring-2 focus:ring-[#002FA7]" />
              </label>
              <label className="block">
                <span className="label-eyebrow block mb-2">Referente</span>
                <input data-testid="customer-contact" value={form.contact} onChange={(e) => setForm({ ...form, contact: e.target.value })} className="w-full px-3 py-2 border border-neutral-300 text-sm focus:outline-none focus:ring-2 focus:ring-[#002FA7]" />
              </label>
              <label className="block">
                <span className="label-eyebrow block mb-2">Telefono</span>
                <input data-testid="customer-phone" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="w-full px-3 py-2 border border-neutral-300 text-sm focus:outline-none focus:ring-2 focus:ring-[#002FA7]" />
              </label>
              <label className="block md:col-span-2">
                <span className="label-eyebrow block mb-2">Email</span>
                <input type="email" data-testid="customer-email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="w-full px-3 py-2 border border-neutral-300 text-sm focus:outline-none focus:ring-2 focus:ring-[#002FA7]" />
              </label>
              <label className="block md:col-span-2">
                <span className="label-eyebrow block mb-2">Template DDT emesso <span className="text-red-600">*</span></span>
                <select required data-testid="customer-ddt-template"
                  value={form.ddt_email_template_id}
                  onChange={(e) => setForm({ ...form, ddt_email_template_id: e.target.value })}
                  className="w-full px-3 py-2 border border-neutral-300 text-sm focus:outline-none focus:ring-2 focus:ring-[#002FA7] bg-white">
                  <option value="">Seleziona un template…</option>
                  {templates.map((t) => (
                    <option key={t.id} value={t.id}>
                      {t.is_default ? "★ " : ""}{t.name}
                    </option>
                  ))}
                </select>
                <span className="text-xs text-neutral-500 mt-1 block">
                  Determina oggetto, corpo e destinatari dell'email inviata quando il DDT viene firmato.{" "}
                  <a href="/settings?tab=ddt-templates" target="_blank" rel="noopener noreferrer" className="text-[#002FA7] hover:underline">Gestisci template →</a>
                </span>
              </label>
              <label className="block md:col-span-2">
                <span className="label-eyebrow block mb-2">Indirizzo</span>
                <input data-testid="customer-address" value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} className="w-full px-3 py-2 border border-neutral-300 text-sm focus:outline-none focus:ring-2 focus:ring-[#002FA7]" />
              </label>
              <label className="block md:col-span-2">
                <span className="label-eyebrow block mb-2">Note</span>
                <textarea data-testid="customer-notes" rows={2} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className="w-full px-3 py-2 border border-neutral-300 text-sm focus:outline-none focus:ring-2 focus:ring-[#002FA7]" />
              </label>
            </div>
            <div className="p-6 pt-0 flex justify-end gap-2">
              <button type="button" onClick={() => setShowForm(false)} className="px-4 py-2 text-sm border border-neutral-300 hover:bg-neutral-100 transition-colors">Annulla</button>
              <button data-testid="customer-submit" type="submit" className="px-4 py-2 text-sm bg-[#002FA7] hover:bg-[#002480] text-white transition-colors">{editing ? "Salva" : "Crea"}</button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}
